import React, { useState } from "react";
import { ArrowRight, Menu, Phone, X } from "lucide-react";
import { Link, NavLink } from "react-router-dom";
import { CONTACT, IMAGES } from "@/config";

const links = [
  { to: "/", label: "Home", end: true },
  { to: "/about/", label: "About" },
];

export const Brand: React.FC<{ className?: string }> = ({ className = "" }) => (
  <Link
    to="/"
    className={`flex items-center gap-3 leading-tight ${className}`}
    data-testid="brand-logo"
  >
    <img
      src={IMAGES.brandLogo}
      alt=""
      className="h-11 w-11 shrink-0 rounded-xl bg-white object-contain"
      width="44"
      height="44"
    />
    <div className="whitespace-nowrap text-xl font-extrabold tracking-tight text-slate-950">
      Simphiwe <span className="text-red-600">Fudwana</span>
    </div>
    <span className="hidden h-9 w-px bg-slate-300 sm:block" />
    <div className="hidden text-[10px] font-semibold leading-[1.25] text-slate-600 sm:block">
      <span className="block">Pre-Owned Vehicle Sales Consultant</span>
      <span className="block text-slate-500">{CONTACT.dealership}</span>
    </div>
  </Link>
);

export const Navbar: React.FC = () => {
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200 bg-white shadow-sm">
      <nav className="container-x flex h-16 items-center justify-between">
        <Brand />

        <div className="hidden items-center gap-10 lg:flex">
          {links.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.end}
              data-testid={`nav-${link.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative py-5 text-sm font-semibold transition-colors ${
                  isActive ? "text-slate-950" : "text-slate-500 hover:text-slate-950"
                } after:absolute after:bottom-0 after:left-0 after:h-0.5 after:rounded-full after:bg-red-600 after:transition-all ${
                  isActive ? "after:w-full" : "after:w-0"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </div>

        <div className="hidden items-center gap-3 lg:flex">
          <Link
            to="/vehicles/"
            className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-5 py-3 text-sm font-bold text-white shadow-[0_8px_20px_-8px_rgba(220,38,38,0.8)] transition-colors hover:bg-red-700"
            data-testid="nav-browse-cars"
          >
            Browse Used Cars <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/contact/"
            className="inline-flex items-center gap-2 rounded-xl border border-red-500 bg-white px-5 py-3 text-sm font-bold text-slate-900 transition-colors hover:bg-red-50"
            data-testid="nav-contact"
          >
            <Phone className="h-4 w-4 text-red-600" /> Get in Touch
          </Link>
        </div>

        <button
          className="rounded-lg p-2 text-slate-900 lg:hidden"
          onClick={() => setOpen((current) => !current)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
          aria-expanded={open}
          aria-controls="mobile-navigation"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      {open && (
        <div id="mobile-navigation" className="border-t border-slate-100 bg-white lg:hidden" data-testid="mobile-menu">
          <div className="container-x flex flex-col gap-2 py-4">
            {links.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.end}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `rounded-lg px-3 py-2.5 text-sm font-semibold ${
                    isActive ? "bg-red-50 text-red-700" : "text-slate-700"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <Link
              to="/vehicles/"
              onClick={() => setOpen(false)}
              className="mt-1 inline-flex items-center justify-center gap-2 rounded-xl bg-red-600 px-5 py-3 text-sm font-bold text-white"
            >
              Browse Used Cars <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/contact/"
              onClick={() => setOpen(false)}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-red-500 px-5 py-3 text-sm font-bold text-slate-900"
            >
              <Phone className="h-4 w-4 text-red-600" /> Get in Touch
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
